import { FormattedMessage, useIntl } from 'react-intl';

import { getLabel } from '@/utils/labelUtils';

import { ProductCard } from '@/components/ProductCard';
import { getProductCardImageAlt } from '@/components/ProductCard/helpers';
import type { OrderAppCtaType } from '@/graphql/orderApp/api';
import { type WatchHit } from '@/integrations/algolia/types';
import type { ProductCatalogModeCtaStyle } from '@/lib/types';
import { theme } from '@/theme';

import { useProductListCardCommon } from './helper';

interface WatchCardProps {
  cardClickHandler?: () => void;
  hideFavorite?: boolean;
  hit: WatchHit;
  productCatalogMode?: boolean;
  productCatalogModeCtaStyle?: ProductCatalogModeCtaStyle;
  sizes?: string;
  style?: React.CSSProperties;
  type?: string;
}

export const WatchCard = ({
  cardClickHandler,
  hideFavorite = false,
  hit,
  productCatalogMode: propProductCatalogMode,
  productCatalogModeCtaStyle = 'DISCOVER',
  sizes,
  style,
  type,
}: WatchCardProps) => {
  const { formatMessage } = useIntl();
  const {
    contextProductCatalogMode,
    detailPath,
    handleClickEvent,
    imageUrl,
    isLocalProcessing,
    isProcessing,
    name,
  } = useProductListCardCommon(hit);

  const productCatalogMode =
    propProductCatalogMode !== undefined
      ? propProductCatalogMode
      : contextProductCatalogMode;

  const handleClick = async (e: React.MouseEvent) => {
    return handleClickEvent(e, undefined, productCatalogMode);
  };

  if (!hit) return null;
  const label = getLabel(hit.marketing_status, hit.launch_date);

  let className;
  switch (type) {
    case 'regular':
      className = 'watch-soldier-image';
      break;
    case 'square':
      className = 'square-card';
      break;
    case 'two-heros':
      className = 'two-heros';
      style = { ...style };
      break;
    default:
      className = 'watch-soldier-image';
  }
  return (
    <ProductCard
      backgroundColor={theme.colors.lightGrey}
      cardClickHandler={cardClickHandler}
      cta={
        (!productCatalogMode || productCatalogModeCtaStyle !== 'HIDDEN') && (
          <ProductCard.CTA
            cta={
              productCatalogMode
                ? 'DISCOVER'
                : (hit.call_to_action as OrderAppCtaType)
            }
            data-test-id='WatchCardProductCardCTA'
            disabled={isProcessing}
            isProcessing={isLocalProcessing}
            onClick={handleClick}
          />
        )
      }
      ctaType='bottom'
      data-test-id='WatchCardProductCard'
      href={detailPath}
      sku={hit.objectID}
      style={style}
      topSlot={
        !hideFavorite && (
          <ProductCard.Favorite
            data-test-id='WatchCardProductCardFavorite'
            name={name}
            price={hit.price}
            sku={hit.objectID}
            theCurrency={hit.currency}
            type={hit.product_type}
          />
        )
      }
      type={hit.product_type}
    >
      <ProductCard.Zone>
        <ProductCard.Image
          alt={getProductCardImageAlt({
            formatMessage,
            name,
            sku: hit.objectID,
          })}
          className={`${className} watch-card`}
          data-test-id='WatchCardProductCardImage'
          loading='lazy'
          productType='watch'
          sizes={sizes ?? '(max-width: 600px) 25vw, 18vw'}
          src={imageUrl ?? ''}
          withBackgroundPlaceholderBackground={false}
        />
      </ProductCard.Zone>
      <ProductCard.Zone>
        <ProductCard.CardZoneLabelPriceRefGroup>
          <ProductCard.Sku data-test-id='WatchCardProductCardSku'>
            {hit.objectID}
          </ProductCard.Sku>
          <ProductCard.Name data-test-id='WatchCardProductCardName'>
            {name}
          </ProductCard.Name>
          {!!hit.price && (
            <ProductCard.Price
              data-test-id='WatchCardProductCardPrice'
              price={{ amount: hit.price, currency: hit.currency }}
            />
          )}
          {label && (
            <ProductCard.Label
              data-test-id='WatchCardProductCardLabel'
              text={<FormattedMessage {...label} />}
            />
          )}
        </ProductCard.CardZoneLabelPriceRefGroup>
      </ProductCard.Zone>
    </ProductCard>
  );
};
